// Tunables for the offline copy and the write queue. Anything the Worker or
// the schemas also need lives in shared/constants.ts; these shape only what
// the browser keeps and how hard it tries to send it.

/** The database is per signed-in account: `project-map-offline-<user id>`. */
export const OFFLINE_DB_PREFIX = 'project-map-offline-';
/** Bumped when the object stores change shape; an older database is deleted and rebuilt. */
export const OFFLINE_DB_VERSION = 1;

/** The object stores, named once so a typo cannot open the wrong one. */
export const OFFLINE_STORES = {
  /** One row per read, keyed by the read's own path (`rooms/<id>`, `rooms/<id>/notes`). */
  entities: 'entities',
  /** The writes waiting for a connection, in the order they were made. */
  queue: 'queue',
  /** Photo thumbnails and files of the properties being kept. */
  blobs: 'blobs',
  /** Which properties are kept, and when the manifest was last read. */
  meta: 'meta',
} as const;

/** The one `meta` row: there is only ever one copy per account. */
export const OFFLINE_META_KEY = 'state';

/** The parent immediately before a cached node in its root-to-node path. */
export const PARENT_PATH_OFFSET = -2;

/** One photo or file this large or larger is named but not kept. */
export const OFFLINE_BLOB_MAX_BYTES = 5 * 1024 * 1024;
/** Everything kept for one property together stops here; the rest is named and skipped. */
export const OFFLINE_PROPERTY_BLOB_MAX_BYTES = 100 * 1024 * 1024;

/** The wait before the first retry of a queued write; each later one doubles it. */
export const OFFLINE_RETRY_BASE_DELAY_MS = 2000;
/** The longest wait between retries, however many have happened. */
export const OFFLINE_RETRY_MAX_DELAY_MS = 5 * 60 * 1000;
/**
 * Attempts a write gets before it is left as failed for a person to look at.
 * A queue that retries for ever hides a problem the technician could fix.
 */
export const OFFLINE_RETRY_MAX_ATTEMPTS = 8;

/** What the toast says when a write went into the queue instead of over the wire. */
export const OFFLINE_SAVED_MESSAGE = 'Saved offline; will sync';

/**
 * The same promise for one action that queued several writes at once:
 * "Saved offline; will sync (3 runs)". Runs made from a pattern are the only
 * action that becomes more than one queued write, and the message above on
 * its own would not say how many of them are waiting. It is built from that
 * message so the words a technician learns to recognise cannot drift.
 */
export function offlineSavedMany(count: number, one: string, many: string): string {
  return `${OFFLINE_SAVED_MESSAGE} (${String(count)} ${count === 1 ? one : many})`;
}
